/* eslint-disable @next/next/no-img-element */
import React, { useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  VideoHeader,
  VideoFooter,
  MediaCard,
  Image,
  Button,
  BUTTON_VARIANTS,
  BUTTON_SIZES,
  Columns,
  Alert,
} from "@gonoodle/gn-universe-ui";
import { generatePath, usePrevious } from "@gonoodle/gn-universe-utils";
import {
  PAGES,
  SECTIONS,
  CONTENT_TYPES,
  SECTIONS_TYPES,
} from "@gonoodle/gn-universe-analytics-schema/src/constants";
import { find, random, compact, isEmpty } from "lodash";
import {
  QueryClient,
  useMutation,
  useQueryClient,
  dehydrate,
} from "@tanstack/react-query";
import { isBefore, subWeeks } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { useParams } from "next/navigation";

import api from "../../api";
import { sendImpression } from "../../hooks/useImpression";
import { useUser } from "../../contexts/user";
import UserSelfIdModal from "../../components/UserSelfIdModal";
import ChampPointsVideoLabel from "../../components/ChampPointsVideoLabel";
import VideoTile from "../../components/VideoTile";
import Layout from "../../components/Layout";
import ShareToGoogleClassroomButton from "../../components/ShareToGoogleClassroomButton";
import {
  useVideoSession,
  useRotatedAdProductOfTypeQuery,
  useImpression,
  useCurrentChampion,
  useVideoDetailsQuery,
  usePreRollSlateQuery,
  useRecommendedVideosQuery,
  useRouter,
} from "../../hooks";
import {
  AD_PRODUCTS_TYPES,
  IMPRESSION_TYPES,
  QUERY_KEYS,
  USER_TYPES,
  ROUTE_PREFIX,
  IMPRESSION_PLACEMENT_TYPES,
  SPONSORED_PARTNER_TYPES,
  ROUTE_PATHS,
  VIDEO_PRE_ROLL_TYPES,
  ROUTE_PERMISSIONS,
  VIDEO_STATES,
} from "../../constants";
import { selectAdProduct } from "../../selectors";
import { withAuthorization } from "../../containers";
import ShareContentButton from "../../components/ShareContentButton";
import { useLogEvent } from "../../contexts/Analytics";
import urls from "../../constants/urls";
import FavoriteButton from "../../components/FavoriteButton";
import { useTransmogrifier } from "../../contexts/Transmogrifier";
import Player from "../../components/Player";
import usePlayer from "../../components/Player/usePlayer";
import Link from "../../components/Link";

const getSponsoredIcon = (attribution) =>
  attribution?.type === "promotional_activity" ? attribution?.icon : null;
const getChannelIconFromTag = (tag) => tag?.images?.icon ?? null;
const getDefaultChannelIcon = (attribution) => attribution?.icon ?? null;

const getSponsoredPartner = ({ attribution, videoId }) =>
  [
    SPONSORED_PARTNER_TYPES.PROMOTIONAL_ACTIVITY,
    SPONSORED_PARTNER_TYPES.LONG_TERM_SPONSOR,
  ].includes(attribution?.type)
    ? {
        name: attribution?.name,
        logo: attribution?.logo,
        sponsorLanguage: attribution?.sponsorLanguage,
        url:
          "https://support.gonoodle.com/article/22-the-gonoodle-sponsor-promise",
        impressionsProps: {
          video_version_id: videoId,
        },
      }
    : null;

const getContentHubPartner = (tag) => {
  const randNum = random(1, 100);
  const adProduct = selectAdProduct(tag?.adProductPartners, randNum);
  const logo = adProduct?.images?.videoPlayerLogo;
  return logo
    ? {
        name: adProduct?.partnerName,
        logo,
        url:
          "https://support.gonoodle.com/article/22-the-gonoodle-sponsor-promise",
        impressionsProps: {
          ad_product_type: adProduct?.type,
          partner_name: adProduct?.partnerName,
          partner_id: adProduct?.partnerId,
          ad_product_partner_id: adProduct?.id,
          content_hub_type: tag?.type,
          content_hub_name: tag?.name,
        },
      }
    : null;
};

const getAdProductPartner = ({ adProductVideoPlayerLogo, videoId }) => {
  const logo = adProductVideoPlayerLogo?.images?.videoPlayerLogo;
  return logo
    ? {
        name: adProductVideoPlayerLogo?.partnerName,
        logo,
        impressionsProps: {
          ad_product_type: adProductVideoPlayerLogo?.type,
          partner_name: adProductVideoPlayerLogo?.partnerName,
          partner_id: adProductVideoPlayerLogo?.partnerId,
          ad_product_partner_id: adProductVideoPlayerLogo?.id,
          video_version_id: videoId,
        },
      }
    : null;
};

const userTypesEligibleForAutoPlay = [
  USER_TYPES.UNREGISTERED_FAMILY.id,
  USER_TYPES.FAMILY.id,
];

const VideoPage = () => {
  const router = useRouter();
  const params = useParams();
  const [selfIdentify, setSelfIdentify] = React.useState(false);
  const [videoState, setVideoState] = React.useState(VIDEO_STATES.LOADING);
  const [isVideoOnFullScreen, setIsVideoOnFullScreen] = React.useState(false);
  const queryClient = useQueryClient();
  const alert = Alert.useAlert();
  const { user } = useUser();
  const recommendedVideosRef = useRef();
  const {
    transition,
    canEarnPoints,
    earnPoints,
    transitionTypes,
    onPointsAdded,
  } = useTransmogrifier();

  const { identity, sourceId } = params;

  const [id] = identity;
  const client = api();
  const prevTransitionStatus = usePrevious(transition);

  const {
    videoDetails = {},
    isVideoDetailsLoading,
    videoDetailsError,
  } = useVideoDetailsQuery(id);

  const {
    jwPlayerId,
    muxPlayerId,
    description,
    transcript,
    title,
    contentTags = [],
    printable,
    id: videoId,
    attribution,
    adTag,
    points: videoPoints,
  } = videoDetails;

  const {
    recommendedVideos,
    isRecommendedVideosLoading,
    recommendedVideosError,
  } = useRecommendedVideosQuery(videoDetails.slug);

  const {
    adProduct: adProductVideoPlayerLogo,
    adProductsError,
  } = useRotatedAdProductOfTypeQuery(AD_PRODUCTS_TYPES.VIDEO_PLAYER_LOGO);

  const tag = useMemo(() => find(videoDetails.contentTags, { id: sourceId }), [
    videoDetails,
    sourceId,
  ]);

  const { preRollSlate, isPreRollSlateLoading } = usePreRollSlateQuery({
    slug: videoId,
    sourceId,
  });

  const calculatedAdTag = useMemo(() => {
    const userTypesEligibleForAds = [
      USER_TYPES.UNREGISTERED_FAMILY.id,
      USER_TYPES.FAMILY.id,
      USER_TYPES.UNREGISTERED_TEACHER.id,
      USER_TYPES.CLASSROOM_TEACHER.id,
    ];

    const userTypesEligibleForPreRollSlate = [
      USER_TYPES.UNREGISTERED_TEACHER.id,
      USER_TYPES.CLASSROOM_TEACHER.id,
    ];

    const cutoff = subWeeks(new Date(), 1);
    const shouldSeeDisclaimer =
      !user.flags.lastDisclaimerSeenAt ||
      isBefore(new Date(user.flags.lastDisclaimerSeenAt), cutoff);

    const adInfo = {
      url: adTag,
      type: VIDEO_PRE_ROLL_TYPES.AD,
    };

    if (shouldSeeDisclaimer) {
      adInfo.url = `${urls.GN_ASSETS_BASE_URL}/pre-roll-bumpers/liability-disclaimer.xml`;
      adInfo.type = VIDEO_PRE_ROLL_TYPES.DISCLAIMER;
      return adInfo;
    }
    if (
      userTypesEligibleForPreRollSlate.includes(user.userTypeId) &&
      preRollSlate
    ) {
      adInfo.url = preRollSlate.adUrl;
      adInfo.type = VIDEO_PRE_ROLL_TYPES.SLATE;
      return adInfo;
    }
    if (!userTypesEligibleForAds.includes(user.userTypeId)) {
      return undefined;
    }
    if (!user.showAd && adTag) {
      adInfo.url = adTag.replace(/(request_type%3D)ad/, `$1blackboard`);
      adInfo.type = VIDEO_PRE_ROLL_TYPES.AD;
    }
    if (tag && adTag) {
      adInfo.url = adInfo.url.replace(/(content_hub%3D)none/, `$1${tag.slug}`);
      adInfo.type = VIDEO_PRE_ROLL_TYPES.AD;
    }
    return adInfo;
  }, [user, tag, adTag, preRollSlate]);

  const channelIcon = useMemo(
    () =>
      getSponsoredIcon(attribution) ??
      getChannelIconFromTag(tag) ??
      getDefaultChannelIcon(attribution),
    [tag, attribution],
  );

  const partner = useMemo(
    () =>
      getSponsoredPartner({ attribution, videoId }) ??
      getContentHubPartner(tag) ??
      getAdProductPartner({ adProductVideoPlayerLogo, videoId }),

    [attribution, videoId, tag, adProductVideoPlayerLogo],
  );

  const partnerLogoInViewRef = useImpression({
    properties: {
      impression_type: IMPRESSION_TYPES.LOGO,
      placement: IMPRESSION_PLACEMENT_TYPES.VIDEO_PLAYER,
      ...partner?.impressionsProps,
    },
    // Only enable ad impressions when there is a partner attached to this video
    // and the `gpt-video-sponsorships` feature flag is disabled
    enabled: !!partner && !user?.features.gptVideoSponsorships,
  });

  const onTagPress = (tagId, tagSlug) => {
    const path = generatePath(ROUTE_PATHS.TAGS, {
      id: tagId,
      slug: tagSlug,
    });

    router.push(path);
  };

  const { mutate: adViewed, isSuccess: adViewedSuccessfully } = useMutation({
    mutationFn: client.adViewed,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.USER],
      });
      if (calculatedAdTag.type === VIDEO_PRE_ROLL_TYPES.SLATE) {
        sendImpression(
          {
            videoCompletionType: "pre_roll",
            adProductType: AD_PRODUCTS_TYPES.PRE_ROLL_SLATE,
            adProductPartnerId: preRollSlate.adProductPartnerId,
            partnerId: preRollSlate.partnerId,
            partnerName: preRollSlate.partnerName,
            videoVersionId: id,
          },
          { event: "video completion" },
        );
      }
    },
  });

  useLogEvent({
    event: "Liability Disclaimer Viewed",
    options: {
      enabled:
        adViewedSuccessfully &&
        calculatedAdTag?.type === VIDEO_PRE_ROLL_TYPES.DISCLAIMER,
      includeReferrer: false,
    },
  });

  const onAdStart = () => {
    if (calculatedAdTag.type === VIDEO_PRE_ROLL_TYPES.SLATE) {
      sendImpression({
        impressionType: IMPRESSION_TYPES.PRE_ROLL,
        adProductType: AD_PRODUCTS_TYPES.PRE_ROLL_SLATE,
        adProductPartnerId: preRollSlate.adProductPartnerId,
        partnerId: preRollSlate.partnerId,
        partnerName: preRollSlate.partnerName,
        videoVersionId: id,
      });
    }
  };

  useEffect(
    () => () => {
      earnPoints();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (
      recommendedVideosRef.current &&
      prevTransitionStatus === transitionTypes.pointAdded &&
      isEmpty(transition)
    ) {
      recommendedVideosRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [prevTransitionStatus, transition, transitionTypes.pointAdded]);

  const videoDetailsProps = {
    title,
    description,
    transcript,
    contentTags,
    printable,
    icon: channelIcon,
    sponsorIcon: partner?.logo,
    sponsorLanguage: partner?.sponsorLanguage,
    partnerUrl: partner?.url,
    partnerName: partner?.name,
  };

  const displayPointsCollectionDialog =
    !!canEarnPoints &&
    !isVideoOnFullScreen &&
    videoState === VIDEO_STATES.COMPLETED &&
    [
      USER_TYPES.UNREGISTERED_TEACHER.id,
      USER_TYPES.CLASSROOM_TEACHER.id,
    ].includes(user.userTypeId);

  const displayPointsCollectionToast =
    !!canEarnPoints &&
    isVideoOnFullScreen &&
    [VIDEO_STATES.COMPLETED, VIDEO_STATES.PAUSED].includes(videoState);

  if (videoDetailsError || recommendedVideosError || adProductsError) {
    alert.publish("Something went wrong. Please reload page.");
  }

  if (isVideoDetailsLoading || isPreRollSlateLoading || videoDetailsError) {
    return null;
  }

  return (
    <Layout
      pageTitle={title}
      pageDescription={description}
      canonicalLinkPath={generatePath(ROUTE_PATHS.VIDEOS, {
        id,
        slug: videoDetails.slug,
      })}
      videoState={videoState}
      progressBar
    >
      <div className="container max-w-5xl mx-auto md:pt-md">
        <VideoHeader
          videoDetails={videoDetailsProps}
          ref={partnerLogoInViewRef}
        />

        <Player
          playbackId={jwPlayerId}
          playlistId={
            userTypesEligibleForAutoPlay.includes(user.userTypeId)
              ? process.env.DEPLOY_ENV === "production"
                ? "Sx7rwJe6"
                : "qdA9AhtJ"
              : undefined
          }
          autoPlay={!user.isAnonymous}
          fallback={
            muxPlayerId && {
              playbackId: muxPlayerId,
            }
          }
          adTagUrl={calculatedAdTag?.url}
          onPlay={() => setVideoState(VIDEO_STATES.PLAYING)}
          onPause={() => setVideoState(VIDEO_STATES.PAUSED)}
          onComplete={() => setVideoState(VIDEO_STATES.COMPLETED)}
          onPlayAttempt={() => {
            if (user.isAnonymous) {
              setSelfIdentify(true);
              return false;
            }

            return true;
          }}
          onFullscreenChange={setIsVideoOnFullScreen}
          onAdComplete={() => adViewed(calculatedAdTag.type)}
          onAdStart={onAdStart}
        >
          {() => (
            <>
              <VideoSession
                videoId={videoId}
                videoTitle={title}
                onPointsAwarded={onPointsAdded}
              />

              <UserSelfIdModal
                isOpen={selfIdentify}
                onClose={() => setSelfIdentify(false)}
                analyticsProps={{
                  contentType: CONTENT_TYPES.VIDEO,
                  id: videoId,
                  title: title.toLowerCase(),
                }}
              />

              {displayPointsCollectionDialog && <PointsCollectionOverlay />}

              {displayPointsCollectionToast && <PointsCollectionToast />}
            </>
          )}
        </Player>

        <VideoFooter
          videoDetails={videoDetailsProps}
          printableMediaCard={
            <Link
              href={`/${ROUTE_PREFIX.ACTIVITIES}/${printable?.id}/${printable?.slug}`}
              referrer={{
                sourcePage: `${videoDetails.slug} detail page`,
                sourcePageType: PAGES.VIDEO_DETAIL,
                sourceElement: SECTIONS_TYPES.SHELF,
                sourceName: SECTIONS.PAIRED_PRINTABLE,
              }}
            >
              <MediaCard
                description={printable?.description}
                slug={printable?.slug}
                title={printable?.title}
                images={{ tile: printable?.image }}
                layout="tall"
              />
            </Link>
          }
          onClick={onTagPress}
          actions={
            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between">
              <ChampPointsVideoLabel
                className="max-sm:mt-4"
                points={videoPoints}
              />

              <div className="flex flex-col space-y-sm sm:flex-row sm:space-x-sm sm:space-y-0">
                <FavoriteButton
                  variant="button"
                  type={CONTENT_TYPES.VIDEO}
                  id={id}
                  title={title}
                />

                <ShareToGoogleClassroomButton
                  id={id}
                  title={title}
                  contentType={CONTENT_TYPES.VIDEO}
                />

                <ShareContentButton
                  id={id}
                  title={title}
                  contentType={CONTENT_TYPES.VIDEO}
                />
              </div>
            </div>
          }
        />
      </div>

      {isRecommendedVideosLoading && (
        <Columns
          title="You might like..."
          cols={4}
          loading={true}
          bgColor="bg-gray-800"
        />
      )}

      {recommendedVideos?.length ? (
        <Columns
          ref={recommendedVideosRef}
          title="You might like..."
          cols={4}
          bgColor="bg-gray-800"
        >
          {recommendedVideos.map((video) => (
            <li key={video.slug}>
              <VideoTile
                duration={video.duration}
                sponsor={{
                  expiration: video.attribution?.expiration,
                  type: video.attribution?.type,
                }}
                title={video.title}
                description={video.description}
                slug={video.slug}
                images={video.images}
                id={video.id}
                referrer={{
                  sourcePage: `${videoDetails.slug} detail page`,
                  sourcePageType: PAGES.VIDEO_DETAIL,
                  sourceElement: SECTIONS_TYPES.SHELF,
                  sourceName: SECTIONS.YOU_MIGHT_LIKE,
                }}
              />
            </li>
          ))}
        </Columns>
      ) : null}
    </Layout>
  );
};

const VideoSession = ({ videoId, videoTitle, onPointsAwarded }) => {
  const session = useVideoSession({ videoId, onPointsAwarded });
  const { playerType } = usePlayer();
  const [isAutoPlayed, setIsAutoPlayed] = React.useState(false);

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setIsAutoPlayed(!!urlParams.get("autoPlayed"));
  }, []);

  useLogEvent({
    event: "Video Played",
    properties: {
      player: playerType,
      title: videoTitle,
      id: videoId,
      brainBreakSessionId: session?.activitySessionId,
      contentType: CONTENT_TYPES.VIDEO,
      autoPlayed: isAutoPlayed,
    },
    options: {
      enabled: !!session?.activitySessionId,
    },
  });

  return null;
};

const PointsCollectionOverlay = () => {
  const { setTransition, earnPoints, transitionTypes } = useTransmogrifier();

  return (
    <div className="relative w-full h-full pointer-events-auto">
      <div className="absolute bg-gray-900 opacity-70 inset-0" />
      <div className="flex flex-col justify-center items-center h-full space-y-lg transform">
        <p className="text-xl max-w-screen-sm text-center leading-10 font-bold text-white">
          Great job! Do you want to collect your earned points?
        </p>
        <Button
          variant={BUTTON_VARIANTS.dark}
          className="bg-purple"
          size={BUTTON_SIZES.md}
          onPress={() => {
            setTransition(transitionTypes.pointAdded);
            earnPoints();
          }}
        >
          Collect points
        </Button>
      </div>
    </div>
  );
};

const PointsCollectionToast = () => {
  const currentChampion = useCurrentChampion();
  const { points, earnedPoints } = useTransmogrifier();
  const totalPoints = Math.min(
    points + earnedPoints,
    currentChampion?.pointsInLevel,
  );

  return (
    <ol className="w-fit mt-6 ml-auto mr-6">
      <AnimatePresence>
        <motion.li
          role="status"
          className="gn-text-sm bg-gray-900 text-white rounded-lg max-w-full animate-slide-left"
          initial={{ x: 100 }}
          animate={{ x: 0 }}
          exit={{ opacity: 0 }}
        >
          <div className="pr-4 pl-3 flex items-center sm:min-w-[360px]">
            <Image
              className="h-14"
              sources={{
                "regular@1x": currentChampion.images.headshot["1x"],
                "regular@2x": currentChampion.images.headshot["2x"],
                "regular@3x": currentChampion.images.headshot["3x"],
              }}
              alt=""
            />
            <div className="pl-4 w-full py-4">
              <p>
                <span className="font-bold">{currentChampion.name} </span>
                <span className="text-purple-100">
                  earned {earnedPoints} point
                  {earnedPoints === 1 ? "" : "s"}!
                </span>
              </p>
              <p>
                Exit Full Screen mode and go to the Champ progress bar to add
                {earnedPoints === 1 ? " it" : " them"}!
              </p>
              <div className="relative rounded-full overflow-hidden h-4 mt-2 w-full">
                <div className="absolute inset-0 bg-purple opacity-30 rounded-full" />
                <div
                  className="absolute inset-0 right-auto w-[var(--toast-progress)] bg-purple-700 rounded-full"
                  style={{
                    "--toast-progress": `${
                      (totalPoints / currentChampion.pointsPerLevel) * 100
                    }%`,
                  }}
                />
              </div>
            </div>
          </div>
        </motion.li>
      </AnimatePresence>
    </ol>
  );
};

export const getServerSideProps = withAuthorization(async (ctx) => {
  const {
    params: { identity },
    query: { sourceId, message },
  } = ctx;

  const [id] = identity;
  const client = api(ctx);
  const queryClient = new QueryClient();

  try {
    await queryClient.fetchQuery({
      queryKey: [QUERY_KEYS.VIDEO_DETAILS, id],
      queryFn: () => client.getVideoDetails(id),
    });
  } catch (error) {
    if (error?.code === 410 && !message) {
      return {
        redirect: {
          destination: "?message=410",
          permanent: true,
        },
      };
    }

    if ((error?.code === 410 && message) || error?.code === 404) {
      return { notFound: true };
    }
  }

  await queryClient.prefetchQuery({
    queryKey: compact([QUERY_KEYS.AD_PRODUCT_PRE_ROLL_SLATE, id, sourceId]),
    queryFn: () => client.getPreRollSlate(id, sourceId),
  });

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
  };
}, ROUTE_PERMISSIONS.PUBLIC);

VideoSession.propTypes = {
  videoId: PropTypes.string.isRequired,
  videoTitle: PropTypes.string.isRequired,
  onPointsAwarded: PropTypes.func.isRequired,
};

VideoPage.propTypes = {};

export default VideoPage;
